/* eslint-disable max-len */
import type { EnvironmentVariable } from '../../types';

const PAYLOAD_JSON_SCHEMA_PROPERTY = {
  description: 'The workflow values passed from Transcend to your function.',
  type: 'object',
  additionalProperties: false,
  required: ['type', 'dataSubject', 'isTest', 'extras', 'coreIdentifier'],
  properties: {
    coreIdentifier: {
      type: 'object',
      required: ['value'],
      additionalProperties: false,
      properties: {
        value: {
          type: 'string',
          minLength: 1,
          description:
            'The initial, authenticated identifier submitted with the request. This can have any format, such as an email address (e.g. if using email verification) or a user ID.',
          example: '1ea71f3d',
        },
      },
    },
    type: {
      type: 'string',
      minLength: 1,
      enum: [
        'ACCESS',
        'ERASURE',
        'RECTIFICATION',
        'RESTRICTION',
        'BUSINESS_PURPOSE',
        'PLACE_ON_LEGAL_HOLD',
        'REMOVE_FROM_LEGAL_HOLD',
        'AUTOMATED_DECISION_MAKING_OPT_OUT',
        'USE_OF_SENSITIVE_INFORMATION_OPT_OUT',
        'CONTACT_OPT_OUT',
        'SALE_OPT_OUT',
        'TRACKING_OPT_OUT',
        'CUSTOM_OPT_OUT',
        'AUTOMATED_DECISION_MAKING_OPT_IN',
        'USE_OF_SENSITIVE_INFORMATION_OPT_IN',
        'SALE_OPT_IN',
        'TRACKING_OPT_IN',
        'CONTACT_OPT_IN',
        'CUSTOM_OPT_IN',
      ],
      description:
        'The type of DSR received. Only the request types you enable in Transcend will be sent. If your system does not need to perform the operation, you can configure the workflow such that the webhook is never sent for a particular request type in the Manager Datapoints on the Integrations page.',
    },
    dataSubject: {
      type: 'object',
      description: 'Information about the data subject making this request.',
      additionalProperties: false,
      required: ['type'],
      properties: {
        type: {
          type: 'string',
          minLength: 1,
          description:
            'The class of data subject making the request. The possible values are specified by you in your DSR Automation settings.',
          example: 'customer',
        },
      },
    },
    isTest: {
      type: 'boolean',
      description:
        "Whether or not the request is flagged as a test request in the platform. Your normally don't need to worry about this parameter.",
    },
    extras: {
      type: 'object',
      additionalProperties: false,
      required: ['profile', 'request', 'organization', 'dataSilo'],
      properties: {
        request: {
          type: 'object',
          additionalProperties: false,
          description: 'Additional information about the DSR being processed.',
          required: ['details', 'id', 'link', 'createdAt', 'locale', 'origin'],
          properties: {
            details: {
              type: 'string',
              description: 'Free-form text added to the request',
            },
            id: {
              type: 'string',
              minLength: 1,
              format: 'uuid',
              description: 'A universally unique identifier for the request',
            },
            link: {
              type: 'string',
              minLength: 1,
              description: 'URL path to the request in Transcend',
            },
            createdAt: {
              type: 'string',
              minLength: 1,
              example: '2022-03-04T02:50:42.257Z',
              description: 'When the request was made',
            },
            locale: {
              type: 'string',
              minLength: 1,
              description: 'The language code (IETF BCP 47)',
            },
            country: {
              type: 'string',
              minLength: 1,
              description: 'Country of request',
            },
            countrySubDivision: {
              type: 'string',
              minLength: 1,
              description: 'Sub division of request',
            },
            origin: {
              type: 'string',
              minLength: 1,
              description: 'Which channel the request came inbound from',
              enum: ['PRIVACY_CENTER', 'ADMIN_DASHBOARD', 'API', 'SHOPIFY'],
            },
            attributes: {
              type: 'array',
              uniqueItems: true,
              description:
                'Key-value pairs used to label data subject requests. These are Custom Fields in Transcend, and formerly known as Attributes.',
              items: {
                type: 'object',
                additionalProperties: false,
                properties: {
                  key: {
                    type: 'string',
                    description:
                      'The name of the custom field to key by. This comes from the custom fields defined in the Admin Dashboard at https://app.transcend.io/infrastructure/attributes',
                    minLength: 1,
                  },
                  values: {
                    type: 'array',
                    description:
                      'The values of the custom field to associate with the request.',
                    uniqueItems: true,
                    minItems: 1,
                    items: {
                      type: 'string',
                    },
                  },
                },
              },
            },
          },
        },
        organization: {
          type: 'object',
          description:
            'Information about the Transcend organization this is coming from. This is only important if you have multiple organizations.',
          additionalProperties: false,
          required: ['id', 'uri', 'name'],
          properties: {
            id: {
              type: 'string',
              minLength: 1,
              format: 'uuid',
              description:
                'A universally unique identifier for your organization',
            },
            uri: {
              type: 'string',
              minLength: 1,
              description: 'The URI for your organization',
            },
            name: {
              type: 'string',
              minLength: 1,
              description: 'The human-readable name of your organization.',
            },
          },
        },
        profile: {
          type: 'object',
          description:
            'The profile that your system should look up. Note: this field is present in the DSR job webhook but not the preflight job.',
          additionalProperties: false,
          required: ['identifier', 'type', 'RequestDataSiloId', 'id'],
          properties: {
            identifier: {
              type: 'string',
              minLength: 1,
              description: 'The identifier your system should look up.',
            },
            type: {
              type: 'string',
              minLength: 1,
              example: 'email',
              description:
                'A descriptor for the identifier, such as "email" or "phone". For the enum of possible values, refer to the identifiers enabled in this integration\'s DSR Automation settings.',
            },
            RequestDataSiloId: {
              type: 'string',
              minLength: 1,
              format: 'uuid',
              description: 'A Transcend-generated ID for this lookup.',
            },
            id: {
              type: 'string',
              minLength: 1,
              description:
                'A Transcend-generated UUID for this profile. This is NOT the identifier you should use to search for a profile in your systems (see `profile.identifier`)',
              format: 'uuid',
            },
          },
        },
        dataSilo: {
          type: 'object',
          description:
            'Information about the associated integration in Transcend. Note: this field is present in the DSR job webhook but not the preflight job.',
          additionalProperties: false,
          required: ['id', 'title', 'description', 'link'],
          properties: {
            id: {
              type: 'string',
              minLength: 1,
              format: 'uuid',
              description: 'The ID of this integration in Transcend.',
            },
            title: {
              type: 'string',
              minLength: 1,
              description: 'The title for this integration.',
            },
            description: {
              type: 'string',
              minLength: 1,
              description: 'A description of this integration',
            },
            link: {
              type: 'string',
              minLength: 1,
              description: 'URL path to this integration in Transcend.',
            },
          },
        },
        purpose: {
          type: 'object',
          additionalProperties: false,
          description:
            'When a DSR is triggered as a result of a consent preference change event, this field will include the purpose and preference information that triggered the workflow.',
          required: ['purpose', 'enabled'],
          properties: {
            purpose: {
              type: 'string',
              minLength: 1,
              description:
                'The purpose that was changed, triggering this event.',
            },
            enabled: {
              type: 'boolean',
              description: 'Whether the purpose is enabled or disabled.',
            },
            preferences: {
              type: 'array',
              description:
                'The preferences that were included with the purpose change. This includes both the preferences that were changed as well as any existing preferences that were on the record at the time that the purpose was changed.',
              items: {
                type: 'object',
                additionalProperties: false,
                required: ['topic', 'choice'],
                properties: {
                  topic: {
                    type: 'string',
                    minLength: 1,
                    description: 'The topic of the preference.',
                  },
                  choice: {
                    type: 'object',
                    additionalProperties: false,
                    description: 'The choice of the preference.',
                    properties: {
                      selectValue: {
                        type: 'string',
                        minLength: 1,
                        description: 'The selected value for the preference.',
                      },
                      selectValues: {
                        type: 'array',
                        description: 'The selected values for the preference.',
                        items: {
                          type: 'string',
                        },
                      },
                      booleanValue: {
                        type: 'boolean',
                        description:
                          'The selected boolean value for the preference.',
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  },
};
/* eslint-enable max-len */

/**
 * Get the JSON schema for the custom function
 *
 * @param envVars - environment variables for the custom function
 * @returns the JSON schema
 */
export function getCustomFunctionJsonSchema(
  envVars: EnvironmentVariable[],
): object {
  const environmentJsonSchemaProperty = {
    type: 'object',
    description:
      'The environment variables you have supplied to your custom function.',
    additionalProperties: {
      type: 'string',
    },
    properties: envVars.reduce((acc, envVar) => {
      acc[envVar.name] = {
        type: 'string',
        description:
          'An environment variable set by you on the Environment Variables tab of your Custom Function.',
      };
      return acc;
    }, {}),
  };

  const schema = {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    additionalProperties: false,
    required: ['payload', 'environment'],
    properties: {
      environment: environmentJsonSchemaProperty,
      payload: PAYLOAD_JSON_SCHEMA_PROPERTY,
    },
  };

  return schema;
}
